import { css, styled } from 'styled-components';

import { ButtonOrLink } from '#/components/ButtonOrLink/ButtonOrLink';
import { colors, cssBreakpoints, shadows, transition } from '#/theme/theme';
import { getRgbaString } from '#/utils/style';

import {
  searchHitOneWhenHoverEffectVisibleVar,
  searchHitTopBorderRadiusVar,
} from './searchHitCssVars';

export const SearchHitHolder = styled.div`
  container-type: inline-size;
  display: flex;
  flex-direction: column;
`;

export const SearchHitCard = styled.div`
  position: relative;
`;

type SearchCardInnerStyleProps = {
  $highlighted: boolean;
};

const getSearchHitCardInnerStyles = (
  styleProps: SearchCardInnerStyleProps,
) => css<SearchCardInnerStyleProps>`
  color: inherit;
  text-decoration: none;
  box-shadow: ${styleProps.$highlighted
    ? `0 0 0 2px rgba(${getRgbaString(colors.secondarySkyBlue, 1)})`
    : shadows.low};
  transition: ${transition};
  background: ${colors.white};
  border-radius: 4px;

  pointer-events: ${(cardProps) =>
    cardProps.$highlighted ? 'none' : undefined};

  &:hover {
    color: unset;
  }

  &:hover,
  &:focus {
    ${(cardProps) =>
      !cardProps.$highlighted &&
      css`
        ${searchHitOneWhenHoverEffectVisibleVar}: 1
      `};
  }
`;

export const SearchHitCardInner = styled.div<SearchCardInnerStyleProps>`
  ${getSearchHitCardInnerStyles};

  display: flex;
  flex-direction: column;

  @container (min-width: ${cssBreakpoints.smUp}) {
    flex-direction: row;
    --actions-radius: 0 4px 4px 0;
  }
`;

export const SearchHitActions = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 30px;
  padding: 30px;

  background: ${colors.backgroundGrey};
  border-radius: var(--actions-radius, 0 0 4px 4px);

  /* NOTE: keep the same actions size for ATS and non-ATS listings */
  min-width: 215px;

  @container (min-width: ${cssBreakpoints.smUp}) {
    flex-direction: column;
  }
`;

export const SearchHitContent = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 20px;
  padding: 20px;

  @media all and (min-width: ${cssBreakpoints.xsUp}) {
    flex-direction: row;
    padding: 30px 40px;
  }

  &:not(:last-child) {
    padding-block-end: 10px;

    @container (min-width: ${cssBreakpoints.smUp}) {
      padding-block-end: 30px;
    }
  }
`;

export const SearchHitLink = styled(ButtonOrLink)<SearchCardInnerStyleProps>`
  ${getSearchHitCardInnerStyles};
  display: block;
  cursor: pointer;
`;

export const SearchHitHoverEffect = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 5px;
  overflow: hidden;
  pointer-events: none;

  border-top-left-radius: var(${searchHitTopBorderRadiusVar});
  border-top-right-radius: var(${searchHitTopBorderRadiusVar});

  &::before {
    content: '';
    display: block;
    width: 100%;
    height: 5px;
    background: linear-gradient(
      to left,
      ${colors.lemon},
      ${colors.brandYellow}
    );
    transition: all ${transition};
    transform: translateY(
      calc((var(${searchHitOneWhenHoverEffectVisibleVar}, 0) - 1) * 100%)
    );
  }
`;

export const SearchHitImageHolder = styled.div<{
  $hiddenOnMobile: boolean;
}>`
  max-width: 100%;
  transition: ${transition};
  transition-property: width;

  width: 80px;
  height: 80px;

  @media all and (max-width: ${cssBreakpoints.xsDown}) {
    ${(props) => (props.$hiddenOnMobile ? 'none' : undefined)};
  }

  @supports not (container-type: inline-size) {
    @media all and (max-width: ${cssBreakpoints.mdUp}) {
      opacity: 0;
      width: 0;
    }

    @media all and (max-width: ${cssBreakpoints.mdUp}) {
      opacity: 1;
      width: 120px;
      height: 120px;
    }
  }

  @container (max-width: 300px) {
    opacity: 0;
    width: 0;
  }

  @container (min-width: 301px) {
    opacity: 1;
    width: 120px;
    height: 120px;
  }
`;
